import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';

import { JourneyStateService, STAGE_NAMES, TOTAL_STAGES } from './core/journey-state.service';
import { ApiService } from './core/api.service';
import { AudioService } from './core/audio.service';
import { ToastService } from './core/toast.service';
import { AuthService } from './core/auth.service';

type Mode = 'journey' | 'studio' | 'persistency';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent {
  private router = inject(Router);
  private api = inject(ApiService);
  journey = inject(JourneyStateService);
  audio = inject(AudioService);
  toast = inject(ToastService);
  auth = inject(AuthService);

  readonly stageNames = STAGE_NAMES;
  readonly total = TOTAL_STAGES;
  readonly mode = signal<Mode>('journey');

  // The login page renders bare (no topbar / rail / footer).
  readonly isAuthRoute = signal<boolean>(false);

  // Feature flags — Journey, Agent Studio and Persistency are all enabled and
  // shown in the rail. Flip a flag to false to hide it from the UI.
  readonly features = { journey: true, persistency: true, studio: true };

  readonly prospectName = signal<string>('Priya Sharma');
  readonly prospectMeta = signal<string>('Age 32 · Pune · Salaried · English-preferring');

  readonly progressLabel = computed(
    () => `${String(this.journey.current()).padStart(2, '0')} / 0${this.total}`
  );

  constructor() {
    this.syncMode(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.syncMode(e.urlAfterRedirects));
    // Reload the rail's "Following" name/meta whenever the selected prospect
    // changes (e.g. the user clicks a different lead on the dashboard).
    if (this.features.journey) {
      effect(() => {
        const id = this.journey.prospectId();
        if (id) this.loadProspect(id);
      });
    }
  }

  private syncMode(url: string) {
    this.isAuthRoute.set(url.startsWith('/login'));
    if (url.startsWith('/journey')) this.mode.set('journey');
    else if (url.startsWith('/persistency')) this.mode.set('persistency');
    else if (url.startsWith('/studio')) this.mode.set('studio');
  }

  logout() {
    this.auth.logout();
    this.router.navigate(['/login']);
  }

  private loadProspect(id: string) {
    this.api.lead(id).subscribe({
      next: (p) => {
        this.prospectName.set(p.name);
        this.journey.customerName.set(p.name); // shared with the static Journey tabs

        const bits = [
          p.age ? `Age ${p.age}` : null,
          p.city,
          p.smoker === false ? 'Salaried' : null,
          p.language === 'EN' ? 'English-preferring' : 'Hindi-preferring',
        ].filter(Boolean);
        this.prospectMeta.set(bits.join(' · '));
      },
      error: () => {},
    });
  }

  switchMode(m: Mode) {
    this.router.navigate([`/${m}`]);
  }

  goToStage(stage: number) {
    if (this.mode() !== 'journey') this.router.navigate(['/journey']);
    this.journey.go(stage);
  }

  toggleAudio() { this.audio.toggleMute(); }
  prev() { this.journey.prev(); }
  next() { this.journey.next(); }
  reset() { this.journey.reset(); }

  isDone(stage: number) { return stage < this.journey.current(); }
  isCurrent(stage: number) { return stage === this.journey.current(); }
}
